import { createContext, useContext, useEffect, useState } from 'react';
import { getOrders, postOrder } from '../services';
import { CreateOrderModel, OrderProviderValue, OrdersModel, ProductItemModel, StoreProviderValue, UserProviderValue } from '../models';
import { UserContext } from './user.context';
import { toast } from 'react-toastify';
import { PaymentTypes } from '../enums';
import { StoreContext } from './store.context';

export const OrderContext = createContext({} as OrderProviderValue);

export const OrderProvider = ({ children }: any) => {
   const { isAuthenticated, balance, setBalance } = useContext<UserProviderValue>(UserContext);
   const { updateRaffleEntries } = useContext<StoreProviderValue>(StoreContext);

   const [isLoading, setIsLoading] = useState(false);
   const [orders, setOrders] = useState<OrdersModel>({
      pagination: {
         page: 1,
         limit: 5,
         total: null,
      },
      orders: null,
   });

   useEffect(() => {
      if (!isAuthenticated) {
         setOrders({
            pagination: {
               page: 1,
               limit: 5,
               total: null,
            },
            orders: null,
         });
         return;
      }
      fetchOrders();
   }, [orders.pagination.page, isAuthenticated]);

   const fetchOrders = async () => {
      try {
         const ordersResponse = await getOrders(orders.pagination);
         if (ordersResponse.error || !ordersResponse.data) {
            return;
         }

         if (orders.pagination.page > 1) {
            setOrders({
               pagination: { ...orders.pagination, total: ordersResponse?.data.count },
               orders: [...(orders.orders ?? []), ...ordersResponse?.data.orders],
            });
         } else {
            setOrders({
               pagination: { ...orders.pagination, total: ordersResponse?.data.count },
               orders: ordersResponse?.data.orders,
            });
         }
      } catch (error) {
         console.error(error);
      }
   };

   const placeOrder = async (productItem: ProductItemModel, entries: number): Promise<any> => {
      if (!balance || balance.tickets < productItem.price * entries) {
         toast.error('Insufficient balance');
         return;
      }
      const newOrder: CreateOrderModel = {
         productItems: [
            {
               productItemId: productItem.id,
               quantity: entries,
            },
         ],
         paymentType: PaymentTypes.TICKETS,
         coinflowPaymentId: null,
      };

      setIsLoading(true);
      const response = await postOrder(newOrder);

      if (response.error) {
         toast.error(response.error.message ?? 'Failed to place order');
         setIsLoading(false);
         return;
      }

      setBalance({
         ...balance,
         tickets: balance.tickets - productItem.price * entries,
      });

      updateRaffleEntries(entries, productItem.id);

      toast.success('Order placed successfully');
      setIsLoading(false);
      return;
   };

   return (
      <OrderContext.Provider
         value={{
            orders: orders,
            setOrders: setOrders,
            isLoading: isLoading,
            placeOrder: placeOrder,
         }}>
         {children}
      </OrderContext.Provider>
   );
};
